import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { FilesProvider } from '../../../files/providers/files-provider';
import { GuestsProvider, GuestRow } from '../../providers/guests-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose, MatDialogContent, MatDialogModule,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Team } from "../../../common/models/team";
import { Organization } from "../../../common/models/organization";
import { User } from "../../../common/models/user";
import { Lang } from "../../../common/models/lang";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { AsyncPipe, NgForOf, NgIf } from "@angular/common";
import { MatButton, MatButtonModule, MatIconButton } from "@angular/material/button";
import { MatOption } from "@angular/material/autocomplete";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatFormField } from "@angular/material/form-field";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { MatProgressBar, MatProgressBarModule } from "@angular/material/progress-bar";
import { MatTooltip, MatTooltipModule } from "@angular/material/tooltip";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { take } from "rxjs/operators";

@Component({
  selector: 'guests-teams-form',
  providers: [],
  styleUrls: ['./teams.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgForOf,
    NgIf,
    MatButtonModule,
    MatIconButton,
    MatCheckbox,
    MatFormField,
    MatSelectModule,
    MatProgressBarModule,
    MatTooltipModule,
    TranslateModule,
    MatDialogModule,
    MatIconModule,
    AsyncPipe,
    NgxMatSelectSearchModule
  ],
  templateUrl: './teams.html'
})
export class GuestsTeamsFormComponent implements OnInit, OnDestroy, AfterViewInit {
  public isLoading = false;
  public formTitle = '';


  public organisation: Organization| null = null;
  public guestId = 0;
  public user: User = new User({});
  public guestRow: GuestRow | null= null;
  public langId: number = Lang.ID_DEFAULT;

  @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;

  public availableTeams: Team[] = [];
  public teamsSelected: Team[] = [];
  public teamsSelectFormControl: FormControl<Team[]> = new FormControl<Team[]>([]) as FormControl<Team[]>;
  public teamFilterFormControl: FormControl<string> = new FormControl<string>('') as FormControl<string>;
  public filteredTeams: ReplaySubject<Team[]> = new ReplaySubject<Team[]>(1);
  protected _onDestroy = new Subject<void>();

  public removeCoachAthleteRelations = true;
  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    public utils: UtilsProvider,
    public dialog: MatDialogRef<GuestsTeamsFormComponent>,
    public usersProvider: UsersProvider,
    private _toaster: MatSnackBar,
    private _title: Title,
    private _router: Router,
    private _route: ActivatedRoute,
    private _langsProvider: LangsProvider,
    private _translater: TranslateService,
    private _files: FilesProvider,
    private _guestsProvider: GuestsProvider,
    @Inject(MAT_DIALOG_DATA) private _data :any
  ) {
    this.availableTeams = this._data.availableTeams;
    this.organisation = this._data.organization;
    this.formTitle = this._data.formTitle;

    if (this._data.guestRow) {

      this.guestRow = this._data.guestRow;
      this.guestId = this._data.guestRow.guest.id;
      this.user = this._data.guestRow.user;
      this.langId = this._data.guestRow.user.langId;

      this.teamsSelected = this._data.guestRow.teams;
    }
  }

  public ngOnInit() {
    // Charger les coachs filtrés initialement
    this.filteredTeams.next(this.availableTeams.slice());

    // Écouter les changements dans le champ de recherche
    this.teamFilterFormControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterTeams();
      });

    // Initialiser les coachs sélectionnés si nécessaire
    if (this.teamsSelected.length > 0) {
      this.teamsSelectFormControl.setValue(this.teamsSelected);
    }
  }

  public ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public ngAfterViewInit() {
    this.setInitialValue();
  }


  public onSaveClicked(): void {
    if (this.canSave()) {
      this.isLoading = true;
      this.save().subscribe({
        next: (result: GuestRow) => {
          this._toaster.open(this._translater.instant('Coach_saved'), '', {duration: 5000});
          this.isLoading = false;
          this.dialog.close(result);
        },
        error: (error: any) => this.onError(error)
      })
    } else {
      this.touchFields();
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', {duration: 5000});
    }
  }

  public canSave(): boolean {
    return !this.teamsSelectFormControl.invalid;
  }

  private touchFields(): void {
    this.teamsSelectFormControl.markAsTouched();
  }

  private save(): Observable<GuestRow> {
    const guestRow: GuestRow = {
      user: this.guestRow?.user!,
      guest: this.guestRow?.guest!,
      athletes: [],
      athleteNames: [],
      teams: [],
      teamNames: []
    };

    if (this.teamsSelectFormControl.value.length) {
      for (let i = 0; i < this.teamsSelectFormControl.value.length; i++) {

        const team = this.utils.findIn(
          this.teamsSelectFormControl.value[i].id,
          this.availableTeams,
          'id');
        if (team) {
          guestRow.teams.push(team);
        }
      }
    }
    if (!this.organisation || !this.organisation.id) throw new Error('Organisation id is missing');
    return this._guestsProvider.saveGuestTeams(this.organisation.id, this.removeCoachAthleteRelations, guestRow);
  }

  protected setInitialValue() {
    this.filteredTeams
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.multiSelect.compareWith = (a: Team, b: Team) => a && b && a.id === b.id;
      });
  }

  protected filterTeams() {
    if (!this.availableTeams) {
      return;
    }

    const search = this.teamFilterFormControl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredTeams.next(this.availableTeams.slice());
      return;
    }

    this.filteredTeams.next(
      this.availableTeams.filter((team) =>
        `${team.name}`.toLowerCase().includes(search)
      )
    );
  }


  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 422) {
      if (error.error.errors && error.error.errors.email) {
        // c'est qu'on a essayé de faire valider un email test@test par exemple (c'est validé par le validator angular mais pas laravel...)
        return;
      }
    }

    this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
  }

}
